import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Drawer, Input, Spin } from 'antd';
import {
  CheckOutlined, CloseOutlined, CompassOutlined, QuestionCircleOutlined, SendOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { askHelp } from '../api/client';
import { getHelpGuide, HelpStep } from './helpGuides';

type HelpMessage = { role: 'user' | 'assistant'; content: string };

interface Props {
  view: string;
  userId?: string;
  open: boolean;
  onClose: () => void;
  tourRequestId: number;
  onOpenResources: () => void;
}

const clean = (value: string | null | undefined) => (value || '').replace(/\s+/g, ' ').trim();

const visible = (element: Element) => {
  const rect = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);
  return !!rect && rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden'
    && rect.right > 0 && rect.left < window.innerWidth;
};

const screenLabels = () => {
  const main = document.querySelector('.praxys-main');
  if (!main) return { headings: [], actions: [] };
  const labels = (selector: string, attribute?: string, limit = 12) => Array.from(main.querySelectorAll(selector))
    .filter(visible)
    .map(element => clean(attribute ? element.getAttribute(attribute) : element.textContent).slice(0, 100))
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
    .slice(0, limit);
  return {
    headings: labels('h1, h2, h3'),
    actions: labels('button[aria-label]', 'aria-label', 20),
  };
};

const HelpAssistant: React.FC<Props> = ({ view, userId, open, onClose, tourRequestId, onOpenResources }) => {
  const guide = getHelpGuide(view);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<HelpMessage[]>([]);
  const [asking, setAsking] = useState(false);
  const [steps, setSteps] = useState<HelpStep[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const lastTourRequest = useRef(0);
  const requestId = useRef(0);

  useEffect(() => {
    requestId.current += 1;
    setMessages([]);
    setInput('');
    setSteps([]);
    setAsking(false);
  }, [view]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ block: 'nearest' }); }, [messages, asking]);

  const startTour = useCallback(() => {
    const available = guide.steps.filter(step => {
      const target = document.querySelector(step.target);
      return target && visible(target);
    });
    if (!available.length) return;
    onClose();
    setSteps(available);
    setStepIndex(0);
  }, [guide, onClose]);

  useEffect(() => {
    if (!tourRequestId || tourRequestId === lastTourRequest.current) return;
    lastTourRequest.current = tourRequestId;
    startTour();
  }, [tourRequestId, startTour]);

  useEffect(() => {
    if (view !== 'home' || !userId) return;
    const key = `praxys.help.tour.v1.${userId.toLowerCase()}`;
    try {
      if (localStorage.getItem(key)) return;
    } catch { return; }
    const timer = window.setTimeout(() => {
      try { localStorage.setItem(key, 'shown'); } catch { /* Storage can be disabled. */ }
      startTour();
    }, 1300);
    return () => window.clearTimeout(timer);
  }, [view, userId, startTour]);

  const activeStep = steps[stepIndex];
  useEffect(() => {
    if (!activeStep) { setTargetRect(null); return; }
    const target = document.querySelector(activeStep.target);
    target?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    const update = () => setTargetRect(target?.getBoundingClientRect() || null);
    update();
    const timer = window.setTimeout(update, 250);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [activeStep]);

  useEffect(() => {
    if (!activeStep) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSteps([]);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeStep]);

  const send = async (suggestion?: string) => {
    const question = clean(suggestion ?? input);
    if (!question || asking) return;
    const previous = messages;
    const currentRequest = ++requestId.current;
    setMessages([...previous, { role: 'user', content: question }]);
    setInput('');
    setAsking(true);
    try {
      const labels = screenLabels();
      const response = await askHelp({
        question,
        view,
        page_title: guide.title,
        summary: guide.summary,
        common_tasks: guide.commonTasks,
        ...labels,
        history: previous.slice(-6),
      });
      if (requestId.current === currentRequest) {
        setMessages(current => [...current, { role: 'assistant', content: response.data.reply }]);
      }
    } catch (error) {
      console.error('[Praxys Help] request failed', error);
      if (requestId.current === currentRequest) {
        setMessages(current => [...current, {
          role: 'assistant', content: 'I could not reach Praxys Help right now. Please try again in a moment.',
        }]);
      }
    } finally {
      if (requestId.current === currentRequest) setAsking(false);
    }
  };

  const cardWidth = Math.min(340, window.innerWidth - 32);
  const cardLeft = targetRect ? Math.max(16, Math.min(targetRect.left + 8, window.innerWidth - cardWidth - 16)) : 16;
  const cardTop = targetRect
    ? Math.max(16, Math.min(targetRect.height > 150 ? targetRect.top + 20 : targetRect.bottom + 12, window.innerHeight - 230))
    : 24;

  return (
    <>
      <Drawer
        title={<span><QuestionCircleOutlined /> Praxys Help</span>}
        placement="right"
        width="min(440px, 100vw)"
        className="praxys-help-drawer"
        open={open}
        onClose={onClose}
        extra={<Button size="small" icon={<CompassOutlined />} onClick={startTour}>Start tour</Button>}
      >
        <div className="praxys-help-assistant">
          <div className="praxys-help-intro">
            <strong>{guide.title}</strong>
            <p>{guide.summary}</p>
          </div>
          <div className="praxys-help-suggestions">
            {guide.commonTasks.map(task => (
              <button key={task} type="button" onClick={() => void send(task)} disabled={asking}>{task}</button>
            ))}
            <button type="button" onClick={() => void send('Explain what I can do on this screen.')} disabled={asking}>
              Explain this screen
            </button>
          </div>
          <div className="praxys-help-conversation" aria-live="polite">
            {messages.length === 0 && <p className="praxys-help-empty">Ask about this page or choose a question above.</p>}
            {messages.map((item, index) => (
              <div key={`${item.role}-${index}`} className={`praxys-help-message praxys-help-message--${item.role}`}>
                <strong>{item.role === 'user' ? 'You' : 'Praxys Help'}</strong>
                {item.role === 'assistant'
                  ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.content}</ReactMarkdown>
                  : <p>{item.content}</p>}
              </div>
            ))}
            {asking && <div className="praxys-help-thinking"><Spin size="small" /> Thinking...</div>}
            <div ref={chatEndRef} />
          </div>
          <div className="praxys-help-compose">
            <Input.TextArea
              value={input}
              rows={2}
              maxLength={2000}
              placeholder="Ask how to use Praxys..."
              onChange={event => setInput(event.target.value)}
              onPressEnter={event => {
                if (!event.shiftKey) { event.preventDefault(); void send(); }
              }}
            />
            <Button type="primary" aria-label="Send help question" title="Send help question" icon={<SendOutlined />}
              disabled={asking || !input.trim()} onClick={() => void send()} />
          </div>
          <Button type="link" className="praxys-help-resources" onClick={() => { onClose(); onOpenResources(); }}>
            Help resources
          </Button>
        </div>
      </Drawer>
      {activeStep && (
        <div className="praxys-tour-layer" role="dialog" aria-modal="true" aria-label={`${guide.title} tour`}>
          {!targetRect && <div className="praxys-tour-dim" />}
          {targetRect && <div className="praxys-tour-highlight" style={{
            left: targetRect.left - 5, top: targetRect.top - 5,
            width: targetRect.width + 10, height: targetRect.height + 10,
          }} />}
          <div className="praxys-tour-card" style={{ left: cardLeft, top: cardTop, width: cardWidth }}>
            <div className="praxys-tour-card-top">
              <span>{stepIndex + 1} of {steps.length}</span>
              <Button type="text" icon={<CloseOutlined />} aria-label="Close tour" onClick={() => setSteps([])} />
            </div>
            <h3>{activeStep.title}</h3>
            <p>{activeStep.body}</p>
            <div className="praxys-tour-actions">
              <Button disabled={stepIndex === 0} onClick={() => setStepIndex(index => index - 1)}>Back</Button>
              {stepIndex < steps.length - 1
                ? <Button type="primary" onClick={() => setStepIndex(index => index + 1)}>Next</Button>
                : <Button type="primary" icon={<CheckOutlined />} onClick={() => setSteps([])}>Done</Button>}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpAssistant;
